import { useQuery } from '@tanstack/react-query';
import { createProvidersComplianceApi } from 'data/providers-compliance';
import type { CourtesyOfListingData } from 'data/providers-compliance/providers.api';
import { DISCLAIMER_INFO_QUERY_KEY } from 'constants/use-query';
import { type TFunction, useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import type { ThemeNames } from '@tenants/types/theme';
import { type StandardStatus } from '@zoocasa/go-search';
import { isSold } from 'types/reso';

export interface ListingComplianceInput {
  providerId: number;
  tenantThemeId: ThemeNames;
  listingId: number;
  brokerage: string | null;
  brokerageEmail: string | null;
  brokeragePhone: string | null;
  lastFetched: Date | null;
  listingAgent: string | null;
  listingAgentContact: string | null;
  originalMlsName: string | null;
  province: string | null;
  standardStatus?: StandardStatus | null;
  buyerInfo?: {
    agentFirstName?: string | null;
    agentLastName?: string | null;
    officeName?: string | null;
  };
}

export interface FormattedListingCompliance {
  courtesyText?: string;
  buyerRepText?: string;
}

/**
 * useListingCompliance Hook
 * 
 * Application Layer hook that fetches and formats listing compliance data.
 * This hook bridges the data layer to presentation.
 * 
 * @example
 * ```tsx
 * const { data: compliance, isLoading } = useListingCompliance({
 *   providerId: listing.providerId,
 *   tenantThemeId: theme.id,
 *   listingId: listing.id,
 *   ...
 * });
 * 
 * <ListingCompliance 
 *   mlsNumber={listing.mlsNum}
 *   courtesyText={compliance?.courtesyText}
 *   buyerRepText={compliance?.buyerRepText}
 * />
 * ```
 */
export function useListingCompliance(
  input: ListingComplianceInput
): { data?: FormattedListingCompliance; isLoading: boolean } {
  const { t } = useTranslation(Namespaces.COMMON);

  // Changes made to not consider MLS_NUMBER_ONLY_PROVIDERS or courtesyProviderIds and to show courtesy text for all feeds.
  const { data: disclaimer, isLoading } = useQuery({
    queryKey: [DISCLAIMER_INFO_QUERY_KEY, input.providerId, input.tenantThemeId, input.listingId],
    queryFn: () => {
      const providerCompliance = createProvidersComplianceApi();
      const courtesyOfListingData: CourtesyOfListingData = {
        brokerage: input.brokerage,
        brokerageEmail: input.brokerageEmail,
        brokeragePhone: input.brokeragePhone,
        lastFetched: input.lastFetched,
        listingAgent: input.listingAgent,
        listingAgentContact: input.listingAgentContact,
        originalMlsName: input.originalMlsName,
        province: input.province,
      };
      return providerCompliance.getDisclaimer(
        input.providerId.toString(),
        input.tenantThemeId,
        courtesyOfListingData
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Format buyer representation — only for sold listings
  const isSoldListing = input.standardStatus ? isSold(input.standardStatus) : false;
  const buyerRepText = isSoldListing && input.buyerInfo
    ? getRepresentedByAgent(
        input.buyerInfo.agentFirstName,
        input.buyerInfo.agentLastName,
        input.buyerInfo.officeName,
        t
      )
    : undefined;

  return {
    data: {
      courtesyText: disclaimer?.CourtesyOf,
      buyerRepText,
    },
    isLoading,
  };
}

/**
 * Helper function to format buyer representation text
 */
function getRepresentedByAgent(
  firstName: string | null,
  lastName: string | null,
  officeName: string | null,
  t: TFunction
): string | undefined {
  if (!firstName && !lastName && !officeName) {
    return undefined;
  }

  const agentName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName;
  let context = undefined;
  if (agentName && officeName) {
    context = 'agent_and_office';
  } else if (agentName) {
    context = 'agent';
  } else if (officeName) {
    context = 'office';
  }

  return t(LOCALE_COMMON_PATHS.BUYER_REPRESENTATION_BUYER_REPRESENTED_BY, {
    agentName,
    officeName,
    context,
    defaultMessage: 'Buyer represented by ',
  });
}
